import moment from 'moment';
import { DateTime, Duration, DurationUnits } from 'luxon';

//
// See https://moment.github.io/luxon/#/
//

/**
 * Parse the given string from ISO 8601
 * @param value
 * @returns a DateTime
 */
export function parse(value: string): DateTime | null {
    if (value) {
        const result = DateTime.fromISO(value);
        return result;
    }
    return null;
}

/**
 * Get the count of minutes from now until the given parsable date time string or DateTime.
 * The result is a Duration where units is in 'minutes'.
 * Note: Future dates are positive and past dates are negative.
 * @param value
 * @returns a Duration
 */
export function getMinutesFromNow(value: DateTime | string): Duration | null {
    if (value) {
        const dateTime: DateTime | null = typeof value === 'string' ? parse(value) : value;

        const now = DateTime.now();
        const result = dateTime ? dateTime.diff(now, 'minutes') : null;
        return result;
    }
    return null;
}

/**
 * Get the count of seconds from now until the given parsable date time string or DateTime.
 * The result is a Duration where units is in 'seconds'.
 * Note: Future dates are positive and past dates are negative.
 * @param value
 * @returns a Duration
 */
export function getSecondsFromNow(value: DateTime | string): Duration | null {
    if (value) {
        const dateTime: DateTime | null = typeof value === 'string' ? parse(value) : value;

        const now = DateTime.now();
        const result = dateTime ? dateTime.diff(now, 'seconds') : null;
        return result;
    }
    return null;
}

/**
 * Get a DateTime from the given dateTime string using the given format.
 * @see https://momentjs.com/docs/#/parsing/string-format/
 * @param dateTime
 * @param format
 * @returns
 */
export function fromFormat(dateTime: string, format: string): Date | null {
    if (dateTime) {
        const result = moment(dateTime, format);
        if (result.isValid()) {
            return result.toDate();
        }
    }
    return null;
}

/**
 * Format the given dateTime using the given format.
 * @see https://momentjs.com/docs/#/displaying/
 * @param dateTime
 * @param format
 * @param fallback
 * @returns
 */
export function toFormat(dateTime: Date | null, format: string, fallback?: string): string {
    if (dateTime) {
        try {
            const result = moment(dateTime).format(format);
            return result;
        } catch (error) {
            console.log(`DateTimeUtils.toFormat(), Error[${error}] on [${dateTime}] with format[${format}]`);
        }
    }
    return fallback ?? '';
}

const DateTimeUtils = {
    parse,
    getMinutesFromNow,
    getSecondsFromNow,
    fromFormat,
    toFormat
};
export default DateTimeUtils;
